import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { DashboardLayout, Skeleton, NavItem } from '@/components';
import { mapToNavItems, sidebarNavigation } from '@/config';
import { buildAddonAppsNavigation, useAddonApps } from '@/features/addon-apps';
import EurodimaLogo from '@/components/assets/EurodimaLogo';
import { useAuth } from '@/lib';
import { UserProvider } from '@/app/providers';
import { hasFeature, User } from '@/domain/authentication';

function filterNavByFeatures(items: NavItem[], user: User | null): NavItem[] {
  if (!user) {
    return [];
  }

  return items
    .filter((item) => {
      if (!item.requiredFeature) return true;
      const req = item.requiredFeature;
      if (Array.isArray(req)) return req.some((f) => hasFeature(user, f));
      return hasFeature(user, req);
    })
    .map((item) => ({
      ...item,
      children: item.children ? filterNavByFeatures(item.children, user) : undefined,
    }));
}


const allowedRoutesWithNoCompany = ['/profile', "/addMyCompany"];

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  const { data: addonApps } = useAddonApps({ isEnabled: isAuthenticated });

  // Static navigation first, the Apps group from the backend at the end
  const filteredNavigation = useMemo(
    () => [
      ...filterNavByFeatures(sidebarNavigation, user),
      ...mapToNavItems(buildAddonAppsNavigation(addonApps ?? [])),
    ],
    [user, addonApps],
  );

  const hasWebappFeature = user ? hasFeature(user, 'webapp') : false;

  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasWebappFeature && !allowedRoutesWithNoCompany.includes(location.pathname)) {
      navigate({ to: '/profile' });
    }
  }, [isAuthenticated, isLoading, navigate, hasWebappFeature, location.pathname]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="full-screen bg-surface flex items-center justify-center">
        <Skeleton className="h-8 w-32" />
      </div>
    );
  }

  return (
    <UserProvider user={user}>
      <DashboardLayout
        sidebarItems={filteredNavigation}
        logo={<EurodimaLogo />}
        showNavigation={hasWebappFeature}
      >
        <Outlet />
      </DashboardLayout>
    </UserProvider>
  );
}

export const Route = createFileRoute('/_app')({
  component: AppLayout,
});
